import { normalizeNote } from '../spending/notes';
import type { PricePoint, ShoppingTripItemLike } from './types';

/** Units that convert to a common base so pack sizes compare (500 g and 1 kg are both per kg). */
const UNITS: Record<string, { base: string; factor: number }> = {
  mg: { base: 'kg', factor: 0.000001 },
  g: { base: 'kg', factor: 0.001 },
  gram: { base: 'kg', factor: 0.001 },
  grams: { base: 'kg', factor: 0.001 },
  kg: { base: 'kg', factor: 1 },
  kgs: { base: 'kg', factor: 1 },
  kilo: { base: 'kg', factor: 1 },
  kilos: { base: 'kg', factor: 1 },
  ml: { base: 'L', factor: 0.001 },
  l: { base: 'L', factor: 1 },
  liter: { base: 'L', factor: 1 },
  liters: { base: 'L', factor: 1 },
  litre: { base: 'L', factor: 1 },
  litres: { base: 'L', factor: 1 },
  pc: { base: 'pc', factor: 1 },
  pcs: { base: 'pc', factor: 1 },
  piece: { base: 'pc', factor: 1 },
  pieces: { base: 'pc', factor: 1 },
  dozen: { base: 'pc', factor: 12 },
};

/** The item's name in comparable form (shared with Spending's note matching). */
export function normalizeItemName(name: string): string {
  return normalizeNote(name);
}

export interface UnitPrice {
  /** Base unit: 'kg', 'L', 'pc', or the user's own unit lowercased ('pack'). */
  unit: string;
  /** The quantity in the base unit (500 g → 0.5). */
  baseQuantity: number;
  /** Centavos per one base unit, rounded. */
  minorPerUnit: number;
}

/** Price per unit, or null when the line has no quantity. No unit counts as pieces. */
export function perUnit(
  priceMinor: number,
  quantity: number | null,
  unit: string | null,
): UnitPrice | null {
  if (quantity == null || !(quantity > 0)) return null;
  const key = (unit ?? 'pc').trim().toLowerCase();
  const known = UNITS[key];
  const base = known?.base ?? key;
  const baseQuantity = quantity * (known?.factor ?? 1);
  if (!(baseQuantity > 0)) return null;
  return { unit: base, baseQuantity, minorPerUnit: Math.round(priceMinor / baseQuantity) };
}

/** Unrounded price per base unit, or the price paid when there is no quantity. */
export function comparableUnitPrice(p: {
  priceMinor: number;
  quantity: number | null;
  unit: string | null;
}): number {
  const u = perUnit(p.priceMinor, p.quantity, p.unit);
  return u ? p.priceMinor / u.baseQuantity : p.priceMinor;
}

/** Trip lines with a price, joined with their trip's date and store, oldest first. */
export function pricePoints(
  lines: readonly (ShoppingTripItemLike & { createdAt: string })[],
  trips: ReadonlyMap<string, { date: string; store: string | null; deletedAt: string | null }>,
): PricePoint[] {
  const out: PricePoint[] = [];
  for (const l of lines) {
    if (l.priceMinor == null) continue;
    const trip = trips.get(l.tripId);
    if (!trip || trip.deletedAt) continue;
    out.push({
      tripId: l.tripId,
      tripItemId: l.id,
      name: l.name,
      normalizedName: l.normalizedName,
      quantity: l.quantity,
      unit: l.unit,
      priceMinor: l.priceMinor,
      store: trip.store,
      date: trip.date,
      createdAt: l.createdAt,
    });
  }
  return out.sort(byWhen);
}

function byWhen(a: PricePoint, b: PricePoint): number {
  return a.date < b.date
    ? -1
    : a.date > b.date
      ? 1
      : a.createdAt < b.createdAt
        ? -1
        : a.createdAt > b.createdAt
          ? 1
          : 0;
}

export interface PriceStats {
  points: PricePoint[];
  last: PricePoint;
  previous: PricePoint | null;
  lowest: PricePoint;
  /**
   * 'unit': every point has a quantity in the same base unit, so stats compare price per unit
   * (pack sizes compare fairly). 'paid': stats compare the price paid.
   */
  basis: 'unit' | 'paid';
  /** Base unit when basis is 'unit'. */
  unit: string | null;
  /** In the basis (centavos per unit, or centavos paid), rounded. */
  lastValue: number;
  lowestValue: number;
  averageValue: number;
  /** last − previous in the basis; null with one point. */
  changeValue: number | null;
  /** Store of the lowest price seen (null if it had none). */
  cheapestStore: string | null;
}

/** Last, lowest and average price, and the change since last time, for one item's points. */
export function priceStats(points: readonly PricePoint[]): PriceStats | null {
  if (points.length === 0) return null;
  const sorted = [...points].sort(byWhen);
  const units = sorted.map((p) => perUnit(p.priceMinor, p.quantity, p.unit));
  const unitBasis = units.every((u) => u !== null) && new Set(units.map((u) => u!.unit)).size === 1;
  const value = (p: PricePoint) => (unitBasis ? comparableUnitPrice(p) : p.priceMinor);
  const last = sorted[sorted.length - 1]!;
  const previous = sorted.length > 1 ? sorted[sorted.length - 2]! : null;
  let lowest = sorted[0]!;
  for (const p of sorted) if (value(p) < value(lowest)) lowest = p;
  const average = sorted.reduce((s, p) => s + value(p), 0) / sorted.length;
  return {
    points: sorted,
    last,
    previous,
    lowest,
    basis: unitBasis ? 'unit' : 'paid',
    unit: unitBasis ? units[0]!.unit : null,
    lastValue: Math.round(value(last)),
    lowestValue: Math.round(value(lowest)),
    averageValue: Math.round(average),
    changeValue: previous ? Math.round(value(last)) - Math.round(value(previous)) : null,
    cheapestStore: cheapestStore(sorted),
  };
}

/** The store with the lowest comparable price seen; null when no point has a store. */
export function cheapestStore(points: readonly PricePoint[]): string | null {
  let best: PricePoint | null = null;
  const comparable = (p: PricePoint) => comparableUnitPrice(p);
  for (const p of points) {
    if (!p.store) continue;
    if (!best || comparable(p) < comparable(best)) best = p;
  }
  return best?.store ?? null;
}

/** Price points grouped by normalized name, each list oldest first. */
export function groupByItem(points: readonly PricePoint[]): Map<string, PricePoint[]> {
  const out = new Map<string, PricePoint[]>();
  for (const p of [...points].sort(byWhen)) {
    const list = out.get(p.normalizedName) ?? [];
    list.push(p);
    out.set(p.normalizedName, list);
  }
  return out;
}

export interface ItemSuggestion {
  name: string;
  normalizedName: string;
  /** The last price paid, or null if it was never bought with its own price. */
  lastPriceMinor: number | null;
  lastQuantity: number | null;
  lastUnit: string | null;
  lastStore: string | null;
}

/**
 * Past items matching what the user is typing (spec §6: "suggestions come from past items as you
 * type, with the last price paid shown"). Names that start with the query come first, then names
 * that contain it; within each, the most recently bought first.
 */
export function suggestItems(
  query: string,
  lines: readonly (Pick<ShoppingTripItemLike, 'name' | 'normalizedName' | 'quantity' | 'unit'> & {
    date: string;
    createdAt: string;
  })[],
  points: readonly PricePoint[],
  limit = 5,
): ItemSuggestion[] {
  const q = normalizeItemName(query);
  if (q === '') return [];
  const latest = new Map<string, (typeof lines)[number]>();
  for (const l of lines) {
    const cur = latest.get(l.normalizedName);
    if (!cur || l.date > cur.date || (l.date === cur.date && l.createdAt > cur.createdAt))
      latest.set(l.normalizedName, l);
  }
  const byName = groupByItem(points);
  const matches = [...latest.values()].filter((l) => l.normalizedName.includes(q));
  matches.sort((a, b) => {
    const ap = a.normalizedName.startsWith(q) ? 0 : 1;
    const bp = b.normalizedName.startsWith(q) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return a.date < b.date ? 1 : a.date > b.date ? -1 : b.createdAt.localeCompare(a.createdAt);
  });
  return matches.slice(0, limit).map((l) => {
    const pts = byName.get(l.normalizedName);
    const last = pts ? pts[pts.length - 1]! : null;
    return {
      name: l.name,
      normalizedName: l.normalizedName,
      lastPriceMinor: last?.priceMinor ?? null,
      lastQuantity: last ? last.quantity : l.quantity,
      lastUnit: last ? last.unit : l.unit,
      lastStore: last?.store ?? null,
    };
  });
}
